import {AfterViewInit, ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {ReactiveFormsModule} from '@angular/forms';
import {FormDialog, FormDialogDefinition} from '@aviary/dialogs/form-dialog/form-dialog.types';
import {FormDialogComponent} from '@aviary/dialogs/form-dialog/form-dialog.component';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {buildFormGroup, getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {DateFormFieldComponent} from '@aviary/forms/fields/date-form-field/date-form-field.component';
import {HoldDetailComponent} from '@app/my-account/holds/hold-detail/hold-detail.component';
import {AllHoldsDialogData, Hold, PauseAllHoldsValue} from '@store/holds/holds.types';
import {HoldsValidators} from '@store/holds/holds.validators';
import {addDays} from 'date-fns';
import {toDateOnlyIsoString, toLocalMidnight} from '@aviary/store/common/date.helpers';
import {DateOnlyIsoString} from '@aviary/store/common/common.types';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';

@Component({
  selector: 'px-pause-all-holds-dialog',
  template: `
    <mx-form-dialog [definition]="dialogDefinition">
      <px-hold-detail class="-mt-2" [hold]="firstHold"/>

      @if (showAdditionalItemsMessage) {
        <p class="text-title-medium my-1">
          {{ additionalItemsMessage }}
        </p>
      }

      <mx-date-form-field fieldName="pauseUntil" overrideLabel="Pause Until" [formGroup]="formGroup"/>
    </mx-form-dialog>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DateFormFieldComponent, FormDialogComponent, HoldDetailComponent, ReactiveFormsModule]
})
export class PauseAllHoldsDialog implements FormDialog<AllHoldsDialogData, PauseAllHoldsValue>, AfterViewInit {

  public readonly dialogData: AllHoldsDialogData = inject(MAT_DIALOG_DATA);
  public readonly firstHold: Hold = this.dialogData.holds[0];

  private readonly additionalItemsCount: number = this.dialogData.totalHoldsCount - 1;
  public readonly showAdditionalItemsMessage: boolean = this.additionalItemsCount > 0;
  public readonly additionalItemsMessage: string = `and ${this.additionalItemsCount} additional item${this.additionalItemsCount > 1 ? 's' : ''}...`;

  public readonly formGroup: ExtendedFormGroup = buildFormGroup(
    {pauseUntil: ''},
    inject(HoldsValidators).pauseAllHolds
  );

  public readonly dialogDefinition: FormDialogDefinition<PauseAllHoldsValue> = {
    title: 'Pause All Items',
    description: 'Some items may not be eligible to pause if they are already en route to your library.',
    confirmButtonLabel: 'Pause All',
    formGroup: this.formGroup
  };

  public ngAfterViewInit(): void {
    const pauseUntilControl: ExtendedFormControl = getExtendedControlFrom(this.formGroup, 'pauseUntil');
    const tomorrowAtMidnight: DateOnlyIsoString = toDateOnlyIsoString(addDays(toLocalMidnight(new Date()), 1));

    pauseUntilControl.patchValue(tomorrowAtMidnight);
    pauseUntilControl.updateValueAndValidity();
  }
}
